import type { Product, Revision } from "../api/types";
import { ErrorNotice, Panel, StatusBadge, formatDateTime, formatMoney } from "../components/ui";
import { useRevisionHistory } from "./useProduct";

/** Revision history, read-only.
 *
 * Only fields the revision response already exposes. No storage keys, no upload
 * batch internals, no audit detail — none of that is in this response and none
 * of it is fetched from elsewhere to fill the table out.
 */
export function HistoryPanel({
  productId,
  product,
  draft,
  active,
}: {
  productId: string;
  product: Product;
  draft: Revision | null;
  active: boolean;
}) {
  const history = useRevisionHistory(productId, active);

  return (
    <Panel
      title={
        <>
          Revision history{" "}
          {history.data ? (
            <span className="workspace__tabCount">{history.data.count}</span>
          ) : null}
        </>
      }
      bodyClass=""
    >
      {history.isError ? (
        <div className="panel__body">
          <ErrorNotice error={history.error} />
        </div>
      ) : history.isPending ? (
        <div className="empty" role="status">
          Loading history…
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th className="num">Revision</th>
              <th>Status</th>
              <th>Name</th>
              <th className="num">Price</th>
              <th>Created</th>
              <th>Published</th>
              <th>Marker</th>
            </tr>
          </thead>
          <tbody>
            {history.data?.revisions.map((revision) => {
              const isActive = revision.id === product.active_revision_id;
              const isDraft = revision.id === draft?.id;
              return (
                <tr key={revision.id}>
                  <td className="num">{revision.revision_number}</td>
                  <td>
                    <StatusBadge status={revision.publication_status} />
                  </td>
                  <td>{revision.name}</td>
                  <td className="num">
                    {formatMoney(revision.base_price, revision.currency)}
                  </td>
                  <td className="muted">{formatDateTime(revision.created_at)}</td>
                  <td className="muted">{formatDateTime(revision.published_at)}</td>
                  <td className="muted">
                    {isActive ? "Active revision" : isDraft ? "Editable draft" : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
